// CategoryOverview.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareListPageMeta } from '../resource/prepareListPageMeta.js';
import UniversalContainer from '../component/UniversalContainer.jsx';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({ context, params, request }) => {
  const log = legacySSRLog.extend('CategoryOverview loader()');
  const errorLog = legacySSRError.extend('CategoryOverview loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    const state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData });

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const canonicalPath = '/category-overview';
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareListPageMeta({
      state,
      titleKey: 'seo_overview_category_title',
      descriptionKey: 'seo_overview_category_description',
      keywordsKey: 'seo_overview_category_keywords',
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      jsonLdType: 'WebSite',
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const CategoryOverviewRoute = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'CategoryOverview',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalContainer filename="MessageCategoryMore" />
    </>
  );
};

export default CategoryOverviewRoute;
